import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Container,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { CheckCircleOutline, Replay, Videocam, CalendarMonth, Close } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DateRangePicker } from 'react-date-range';
import { format, startOfMonth, endOfMonth, subMonths, subDays, isSameDay } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  fetchHrmsDepartments,
  fetchHrmsDesignations,
  getKivoBranches,
  getCompanyPickerConfig,
  getStoredToken,
  registerFaceBase64,
  updateHrmsEmployeeValidity,
  verifyFaceQualityBase64,
} from '../utils/api';
import CapturePreviewCard from './employeeEnrollment/CapturePreviewCard';
import EnrollmentStepper from './employeeEnrollment/EnrollmentStepper';
import WebcamCapture from './employeeEnrollment/WebcamCapture';

const ENROLLMENT_STEPS = [
  { key: 'front', label: 'Front', direction: 'front', required: true },
  // Profile-side capture uses opposite head-turn direction:
  // left profile => turn right, right profile => turn left.
  { key: 'left', label: 'Left', direction: 'right', required: true },
  { key: 'right', label: 'Right', direction: 'left', required: true },
  { key: 'custom', label: 'Top View', direction: 'top', required: false },
];

const REQUIRED_KEYS = ENROLLMENT_STEPS.filter((step) => step.required).map((step) => step.key);
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const HERO_ANIMATION_MS = 460;
const IDENTITY_SIMILARITY_THRESHOLD = 0.38;
const primary = '#40518A';
const EMPTY_QUALITY_RESULTS = {
  front: null,
  left: null,
  right: null,
  custom: null,
};

const EmployeeRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [employeeName, setEmployeeName] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [capturedImages, setCapturedImages] = useState({
    front: null,
    left: null,
    right: null,
    custom: null,
  });
  const [qualityResults, setQualityResults] = useState(EMPTY_QUALITY_RESULTS);

  const [isEnrollmentActive, setIsEnrollmentActive] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [captureSessionNonce, setCaptureSessionNonce] = useState(0);
  const [enrollmentCameraDeviceId, setEnrollmentCameraDeviceId] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [employeeIdError, setEmployeeIdError] = useState(false);

  const [isRegistering, setIsRegistering] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [designationOptions, setDesignationOptions] = useState([]);
  const [selectedDesignation, setSelectedDesignation] = useState('');
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [validity, setValidity] = useState({
    unlimited: true,
    validFrom: '',
    validTill: '',
  });
  const [validityPickerOpen, setValidityPickerOpen] = useState(false);
  const [validityDateRange, setValidityDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: 'selection' },
  ]);
  const [validityStartTime, setValidityStartTime] = useState('00:00');
  const [validityEndTime, setValidityEndTime] = useState('23:59');
  const [captureHero, setCaptureHero] = useState(null);
  const previewFrameRefs = useRef({});
  const heroTimerRef = useRef(null);
  const heroRafRef = useRef(null);
  const employeeIdInputRef = useRef(null);
  const capturedImagesRef = useRef({ front: null, left: null, right: null, custom: null });
  const captureOrderRef = useRef([]);
  const prefillAppliedRef = useRef(false);

  const currentStep = ENROLLMENT_STEPS[activeStep] || ENROLLMENT_STEPS[0];
  const isRedirectRegistration = location?.state?.source === 'audit_train';
  const requiredKeys = isRedirectRegistration ? ['front'] : REQUIRED_KEYS;
  const requiredCaptured = requiredKeys.every((key) => Boolean(capturedImages[key]));

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const pad2 = (value) => String(value).padStart(2, '0');

  const combineLocalDateAndTime = (dateObj, timeHHmm) => {
    const d = new Date(dateObj);
    const parts = String(timeHHmm || '00:00').split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    d.setHours(h, m, 0, 0);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:00`;
  };

  const applyValidityRangePicker = () => {
    const sel = validityDateRange[0];
    if (!sel?.startDate || !sel?.endDate) {
      showSnackbar('Please select a date range', 'warning');
      return;
    }
    const fromStr = combineLocalDateAndTime(sel.startDate, validityStartTime);
    const tillStr = combineLocalDateAndTime(sel.endDate, validityEndTime);
    if (fromStr > tillStr) {
      showSnackbar('Start date/time cannot be after end date/time', 'error');
      return;
    }
    setValidity((prev) => ({
      ...prev,
      validFrom: fromStr,
      validTill: tillStr,
      unlimited: false,
    }));
    setValidityPickerOpen(false);
  };

  const DateRangePickerContent = ({
    dateRange,
    onDateRangeChange,
    disableMaxDate = false,
    showTimeFields = false,
    startTime = '00:00',
    endTime = '23:59',
    onStartTimeChange,
    onEndTimeChange,
  }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        width: showTimeFields ? 'fit-content' : undefined,
        maxWidth: showTimeFields ? '100%' : undefined,
      }}
    >
      <Box sx={{
        minWidth: 140,
        flexShrink: 0,
        borderRight: isMobile ? 'none' : '1px solid',
        borderBottom: isMobile ? '1px solid' : 'none',
        borderColor: 'divider',
        p: 2,
        display: 'flex',
        flexDirection: isMobile ? 'row' : 'column',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: 0.5
      }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, width: isMobile ? '100%' : 'auto' }}>
          QUICK SELECT
        </Typography>
        {[
          { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
          { label: 'Yesterday', getValue: () => ({ startDate: subDays(new Date(), 1), endDate: subDays(new Date(), 1) }) },
          { label: 'Last 7 Days', getValue: () => ({ startDate: subDays(new Date(), 6), endDate: new Date() }) },
          { label: 'Last 30 Days', getValue: () => ({ startDate: subDays(new Date(), 29), endDate: new Date() }) },
          { label: 'This Month', getValue: () => ({ startDate: startOfMonth(new Date()), endDate: new Date() }) },
          { label: 'Last Month', getValue: () => ({ startDate: startOfMonth(subMonths(new Date(), 1)), endDate: endOfMonth(subMonths(new Date(), 1)) }) },
        ].map((preset) => (
          <Button
            key={preset.label}
            size="small"
            variant="text"
            onClick={() => {
              const { startDate, endDate } = preset.getValue();
              onDateRangeChange([{ startDate, endDate, key: 'selection' }]);
            }}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              px: 1.5,
              py: 0.75,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
                color: 'primary.main',
              },
            }}
          >
            {preset.label}
          </Button>
        ))}
      </Box>

      <Box sx={{
        flex: showTimeFields ? '0 1 auto' : 1,
        minWidth: 0,
        p: 2,
        '& .rdrCalendarWrapper': {
          fontSize: '0.85rem',
        },
        '& .rdrMonth': {
          width: { xs: '280px', sm: '300px' },
        },
        '& .rdrDefinedRangesWrapper': {
          display: 'none',
        },
      }}>
        <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Selected Range:</strong>{' '}
            {isSameDay(dateRange[0].startDate, dateRange[0].endDate)
              ? format(dateRange[0].startDate, 'MMM dd, yyyy')
              : `${format(dateRange[0].startDate, 'MMM dd, yyyy')} - ${format(dateRange[0].endDate, 'MMM dd, yyyy')}`}
          </Typography>
        </Box>
        <Box
          sx={{
            width: showTimeFields ? 'max-content' : undefined,
            maxWidth: showTimeFields ? '100%' : undefined,
          }}
        >
          <DateRangePicker
            ranges={dateRange}
            onChange={(ranges) => onDateRangeChange([ranges.selection])}
            moveRangeOnFirstSelection={false}
            months={isMobile ? 1 : 2}
            direction={isMobile ? 'vertical' : 'horizontal'}
            rangeColors={[primary]}
            staticRanges={[]}
            inputRanges={[]}
            {...(!disableMaxDate ? { maxDate: new Date() } : {})}
          />
          {showTimeFields && (
            <Box
              sx={{
                px: 0,
                pb: 2,
                pt: 1,
                display: 'flex',
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <TextField
                label="Start Time"
                type="time"
                size="small"
                value={startTime}
                onChange={(e) => onStartTimeChange?.(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                fullWidth
              />
              <TextField
                label="End Time"
                type="time"
                size="small"
                value={endTime}
                onChange={(e) => onEndTimeChange?.(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                fullWidth
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  const firstMissingRequiredStepIndex = useMemo(() => {
    const missing = ENROLLMENT_STEPS.findIndex(
      (step) => step.required && !capturedImages[step.key]
    );
    return missing >= 0 ? missing : ENROLLMENT_STEPS.length - 1;
  }, [capturedImages]);

  const isStepSelectable = (index) => {
    const step = ENROLLMENT_STEPS[index];
    if (!step) return false;
    if (capturedImages[step.key]) return true;
    return index <= firstMissingRequiredStepIndex;
  };

  const startEnrollment = (preferredStepIndex = null) => {
    const stepToStart =
      preferredStepIndex !== null
        ? preferredStepIndex
        : ENROLLMENT_STEPS.findIndex((step) => step.required && !capturedImages[step.key]);

    setActiveStep(stepToStart >= 0 ? stepToStart : ENROLLMENT_STEPS.length - 1);
    setCameraError('');
    setIsEnrollmentActive(true);
  };

  const stopEnrollment = () => {
    setIsEnrollmentActive(false);
  };

  const restartCurrentCaptureSession = () => {
    setCaptureSessionNonce((previous) => previous + 1);
  };

  const markStepCaptured = (stepKey) => {
    captureOrderRef.current = [
      ...captureOrderRef.current.filter((key) => key !== stepKey),
      stepKey,
    ];
  };

  const removeStepFromCaptureOrder = (stepKey) => {
    captureOrderRef.current = captureOrderRef.current.filter((key) => key !== stepKey);
  };

  const getLastCapturedReferenceImage = (excludeStepKey = null) => {
    const order = captureOrderRef.current;
    const currentCaptured = capturedImagesRef.current;
    for (let index = order.length - 1; index >= 0; index -= 1) {
      const key = order[index];
      if (excludeStepKey && key === excludeStepKey) continue;
      if (currentCaptured[key]) {
        return currentCaptured[key];
      }
    }
    return null;
  };

  const handleRetake = (stepKey) => {
    const stepIndex = ENROLLMENT_STEPS.findIndex((step) => step.key === stepKey);
    if (stepIndex < 0) return;

    setCapturedImages((previous) => ({
      ...previous,
      [stepKey]: null,
    }));
    setQualityResults((previous) => ({
      ...previous,
      [stepKey]: null,
    }));
    removeStepFromCaptureOrder(stepKey);

    setCameraError('');
    setCaptureSessionNonce((previous) => previous + 1);
    startEnrollment(stepIndex);
  };

  const handleRetakeAll = () => {
    setCapturedImages({ front: null, left: null, right: null, custom: null });
    setQualityResults(EMPTY_QUALITY_RESULTS);
    captureOrderRef.current = [];
    setEnrollmentCameraDeviceId('');
    setActiveStep(0);
    setIsEnrollmentActive(false);
    showSnackbar('All captured photos cleared. Start enrollment again.', 'info');
  };

  const verifyImageQuality = async (stepKey, imageBase64, referenceImageBase64 = null) => {
    if (!stepKey || !imageBase64) return;
    setQualityResults((previous) => ({
      ...previous,
      [stepKey]: {
        loading: true,
        success: false,
        message: 'Checking image quality...',
      },
    }));

    try {
      const result = await verifyFaceQualityBase64(
        imageBase64,
        referenceImageBase64,
        referenceImageBase64 ? IDENTITY_SIMILARITY_THRESHOLD : null
      );
      setQualityResults((previous) => ({
        ...previous,
        [stepKey]: {
          ...result,
          loading: false,
        },
      }));
      return result;
    } catch (error) {
      console.error('Image quality verification failed:', error);
      const errorResult = {
        loading: false,
        success: false,
        face_detected: false,
        face_count: 0,
        message: error.message || 'Failed to verify image quality',
        angle: null,
        pose_quality: 0,
        lighting_quality: 0,
        quality_score: 0,
        similarity: null,
        similarity_threshold: referenceImageBase64 ? IDENTITY_SIMILARITY_THRESHOLD : null,
        is_same_person: null,
        encoding_dimension: 0,
        encoding_preview: [],
      };
      setQualityResults((previous) => ({
        ...previous,
        [stepKey]: errorResult,
      }));
      return errorResult;
    }
  };

  const toRectSnapshot = (rect) => {
    if (!rect) return null;
    const width = Number(rect.width || 0);
    const height = Number(rect.height || 0);
    if (width <= 0 || height <= 0) return null;
    return {
      left: Number(rect.left || 0),
      top: Number(rect.top || 0),
      width,
      height,
    };
  };

  const getContainedImageRect = (containerRect, aspectRatio) => {
    if (!containerRect || !aspectRatio || !Number.isFinite(aspectRatio) || aspectRatio <= 0) {
      return containerRect;
    }

    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    const containerAspect = containerWidth / containerHeight;

    let width;
    let height;

    if (containerAspect > aspectRatio) {
      height = containerHeight;
      width = height * aspectRatio;
    } else {
      width = containerWidth;
      height = width / aspectRatio;
    }

    return {
      left: containerRect.left + ((containerWidth - width) / 2),
      top: containerRect.top + ((containerHeight - height) / 2),
      width,
      height,
    };
  };

  const getImageAspectRatio = (src) =>
    new Promise((resolve) => {
      if (!src) {
        resolve(null);
        return;
      }
      const img = new Image();
      img.onload = () => {
        const width = Number(img.naturalWidth || 0);
        const height = Number(img.naturalHeight || 0);
        if (width > 0 && height > 0) {
          resolve(width / height);
          return;
        }
        resolve(null);
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });

  const handleCaptured = (imageBase64, sourceRect = null) => {
    const capturedStep = currentStep;
    const capturedStepIndex = activeStep;
    const sourceRectSnapshot = toRectSnapshot(sourceRect);

    const finalizeCapture = async () => {
      const referenceImageBase64 = getLastCapturedReferenceImage(capturedStep.key);
      const verification = await verifyImageQuality(
        capturedStep.key,
        imageBase64,
        referenceImageBase64
      );

      if (!verification?.success) {
        showSnackbar(
          verification?.message || 'Image rejected. Please capture the same person only.',
          'error'
        );
        restartCurrentCaptureSession();
        return;
      }

      setCapturedImages((previous) => ({
        ...previous,
        [capturedStep.key]: imageBase64,
      }));
      markStepCaptured(capturedStep.key);

      if (capturedStep.key === 'right') {
        showSnackbar('Right angle captured. Top View is optional and can be captured now.', 'success');
        setActiveStep(3);
        return;
      }

      if (capturedStep.key === 'custom') {
        showSnackbar('Top View captured.', 'success');
        setIsEnrollmentActive(false);
        return;
      }

      const nextStepIndex = Math.min(capturedStepIndex + 1, ENROLLMENT_STEPS.length - 1);
      setActiveStep(nextStepIndex);
      showSnackbar(`${capturedStep.label} captured.`, 'success');
    };

    if (!sourceRectSnapshot) {
      void finalizeCapture();
      return;
    }

    const launchHeroWithRetry = (imageAspectRatio, attempt = 0) => {
      const targetNode = previewFrameRefs.current[capturedStep.key];
      const targetRect = toRectSnapshot(targetNode?.getBoundingClientRect?.() || null);

      if (!targetRect && attempt < 8) {
        heroRafRef.current = requestAnimationFrame(() => launchHeroWithRetry(imageAspectRatio, attempt + 1));
        return;
      }

      if (!targetRect) {
        void finalizeCapture();
        return;
      }

      const heroId = Date.now();
      const startRect = getContainedImageRect(sourceRectSnapshot, imageAspectRatio);
      const endRect = getContainedImageRect(targetRect, imageAspectRatio);
      setCaptureHero({
        id: heroId,
        image: imageBase64,
        startRect,
        endRect,
        active: false,
      });

      heroRafRef.current = requestAnimationFrame(() => {
        setCaptureHero((previous) => {
          if (!previous || previous.id !== heroId) return previous;
          return { ...previous, active: true };
        });
      });

      if (heroTimerRef.current) {
        clearTimeout(heroTimerRef.current);
        heroTimerRef.current = null;
      }

      heroTimerRef.current = setTimeout(() => {
        void (async () => {
          try {
            await finalizeCapture();
          } finally {
            setCaptureHero(null);
          }
        })();
      }, HERO_ANIMATION_MS);
    };

    getImageAspectRatio(imageBase64).then((imageAspectRatio) => {
      launchHeroWithRetry(imageAspectRatio);
    });
  };

  const handleManualUpload = (stepKey) => async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type || !file.type.startsWith('image/')) {
      showSnackbar('Please choose a valid image file.', 'error');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      showSnackbar('Image is too large. Maximum size is 5MB.', 'error');
      return;
    }

    try {
      const stepLabel = ENROLLMENT_STEPS.find((step) => step.key === stepKey)?.label || 'Image';
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const referenceImageBase64 = getLastCapturedReferenceImage(stepKey);
      const verification = await verifyImageQuality(stepKey, base64, referenceImageBase64);
      if (!verification?.success) {
        showSnackbar(
          verification?.message || 'Uploaded image does not match previously captured person.',
          'error'
        );
        restartCurrentCaptureSession();
        return;
      }
      setCapturedImages((previous) => ({
        ...previous,
        [stepKey]: base64,
      }));
      markStepCaptured(stepKey);
      showSnackbar(`${stepLabel} uploaded successfully.`, 'success');
    } catch (error) {
      console.error('Manual upload error:', error);
      showSnackbar('Unable to read selected image. Please try another file.', 'error');
    }
  };

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (prefillAppliedRef.current) return;
    const prefillCaptureImage = location?.state?.prefillCaptureImage;
    if (!prefillCaptureImage || typeof prefillCaptureImage !== 'string') return;
    prefillAppliedRef.current = true;
    setCapturedImages((previous) => ({
      ...previous,
      front: prefillCaptureImage,
    }));
    setQualityResults((previous) => ({
      ...previous,
      front: previous.front || {
        loading: false,
        success: true,
        message: 'Prefilled from audit capture.',
      },
    }));
    markStepCaptured('front');
    if (activeStep === 0) setActiveStep(isRedirectRegistration ? 0 : 1);
  }, [activeStep, isRedirectRegistration, location?.state]);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const res = await getCompanyPickerConfig(permissions);
        const configs = res?.configs || (res ? [res] : []);
        setCompanyOptions(configs);
      } catch (err) {
        console.error('Could not load company options', err);
        setCompanyOptions([]);
      }
    };
    loadCompanies();
  }, [permissions]);

  useEffect(() => {
    const loadBranches = async () => {
      if (!selectedCompanyId) {
        setBranchOptions([]);
        setSelectedBranchId('');
        return;
      }
      try {
        const branchesData = await getKivoBranches(selectedCompanyId);
        const branches = Array.isArray(branchesData?.branches) ? branchesData.branches : [];
        setBranchOptions(branches);
        if (
          selectedBranchId &&
          !branches.some((branch) => String(branch.branch_id) === String(selectedBranchId))
        ) {
          setSelectedBranchId('');
        }
      } catch (error) {
        console.error('Error loading branches:', error);
        setBranchOptions([]);
      }
    };

    loadBranches();
  }, [selectedCompanyId, selectedBranchId]);

  useEffect(() => {
    const loadHrmsOptions = async () => {
      if (!selectedCompanyId) {
        setDesignationOptions([]);
        setDepartmentOptions([]);
        setSelectedDesignation('');
        setSelectedDepartment('');
        return;
      }

      try {
        const branchId = selectedBranchId && selectedBranchId.trim() ? selectedBranchId : null;
        const [designationRes, departmentRes] = await Promise.all([
          fetchHrmsDesignations({
            companyId: selectedCompanyId,
            branchId,
            includeInactive: false,
            limit: 200,
          }),
          fetchHrmsDepartments({
            companyId: selectedCompanyId,
            branchId,
            includeInactive: false,
            limit: 200,
          }),
        ]);

        setDesignationOptions(
          Array.isArray(designationRes?.designations) ? designationRes.designations : []
        );
        setDepartmentOptions(
          Array.isArray(departmentRes?.departments) ? departmentRes.departments : []
        );
      } catch (error) {
        console.error('Error loading designation/department options:', error);
        setDesignationOptions([]);
        setDepartmentOptions([]);
      }
    };

    loadHrmsOptions();
  }, [selectedCompanyId, selectedBranchId]);

  useEffect(() => {
    return () => {
      if (heroTimerRef.current) {
        clearTimeout(heroTimerRef.current);
      }
      if (heroRafRef.current) {
        cancelAnimationFrame(heroRafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    capturedImagesRef.current = capturedImages;
  }, [capturedImages]);

  const registerEmployee = async () => {
    if (!employeeName.trim() || !employeeId.trim() || !employeeCode.trim() || !selectedCompanyId) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    if (!requiredCaptured) {
      showSnackbar(
        isRedirectRegistration
          ? 'Please complete Front capture'
          : 'Please complete Front, Left and Right captures',
        'error'
      );
      return;
    }

    const validFromPayload = validity.unlimited ? null : (validity.validFrom || null);
    const validTillPayload = validity.unlimited ? null : (validity.validTill || null);
    if (!validity.unlimited && !validFromPayload && !validTillPayload) {
      showSnackbar('Please select Valid From or Valid Till', 'error');
      return;
    }
    if (validFromPayload && validTillPayload && validFromPayload > validTillPayload) {
      showSnackbar('Valid From cannot be after Valid Till', 'error');
      return;
    }

    setIsRegistering(true);
    try {
      const companyId = selectedCompanyId || null;
      const branchId = selectedBranchId || null;
      const designation = selectedDesignation || null;
      const department = selectedDepartment || null;
      const code = employeeCode.trim();
      const resolvedPersonId = employeeId.trim();
      const results = [];

      const frontResult = await registerFaceBase64(
        employeeName,
        resolvedPersonId,
        capturedImages.front,
        'jpeg',
        'front',
        null,
        companyId,
        branchId,
        false,
        designation,
        department,
        null,
        null,
        code
      );
      results.push(frontResult);

      if (capturedImages.left) {
        results.push(
          await registerFaceBase64(
            employeeName,
            resolvedPersonId,
            capturedImages.left,
            'jpeg',
            'left',
            null,
            companyId,
            branchId,
            false,
            designation,
            department,
            null,
            null,
            code
          )
        );
      }

      if (capturedImages.right) {
        results.push(
          await registerFaceBase64(
            employeeName,
            resolvedPersonId,
            capturedImages.right,
            'jpeg',
            'right',
            null,
            companyId,
            branchId,
            false,
            designation,
            department,
            null,
            null,
            code
          )
        );
      }

      if (capturedImages.custom) {
        results.push(
          await registerFaceBase64(
            employeeName,
            resolvedPersonId,
            capturedImages.custom,
            'jpeg',
            'custom_angle',
            'Top View',
            companyId,
            branchId,
            false,
            designation,
            department,
            null,
            null,
            code
          )
        );
      }

      const isOk = (r) => !!r && (r.success || /already exists with better quality/i.test(r.message || ''));
      const failed = results.filter((r) => !isOk(r));
      if (failed.length) {
        showSnackbar(failed[0]?.message || 'One or more angles could not be registered.', 'error');
        setIsRegistering(false);
        return;
      }
      if (results.length && results.every(isOk)) {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        await updateHrmsEmployeeValidity(resolvedPersonId, validFromPayload, validTillPayload, tz);

        setEmployeeName('');
        setEmployeeId('');
        setEmployeeCode('');
        setValidity({ unlimited: true, validFrom: '', validTill: '' });
        setValidityDateRange([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
        setValidityStartTime('00:00');
        setValidityEndTime('23:59');
        setCapturedImages({ front: null, left: null, right: null, custom: null });
        setQualityResults(EMPTY_QUALITY_RESULTS);
        captureOrderRef.current = [];
        setEnrollmentCameraDeviceId('');
        setIsEnrollmentActive(false);
        setActiveStep(0);

        navigate('/employees', {
          state: {
            toast: {
              message: 'Employee registered with guided face enrollment successfully!',
              severity: 'success',
            },
          },
        });
      } else {
        showSnackbar('One or more angle uploads failed. Please review captures and retry.', 'error');
        setIsRegistering(false);
      }
    } catch (error) {
      console.error('Error registering employee:', error);

      if (error?.status === 409 && error?.detail?.code === 'FACE_ANGLE_EXISTS') {
        setEmployeeIdError(true);

  setTimeout(() => {
    employeeIdInputRef.current?.focus();
  }, 0);
        showSnackbar(
          error.message || 'This employee already has a face at this angle. Remove it or choose another angle.',
          'warning',
        );
        return;
      }

      // Provide user-friendly error messages
      let userMessage = error.message || 'Failed to register employee. Please try again.';

      if (error.message) {
        if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
          userMessage = 'Registration request timed out. Please check your connection and try again.';
        } else if (
          error.message.includes('Authentication expired') ||
          error.message.includes('No authentication token')
        ) {
          userMessage = 'Your session has expired. Please log in again.';
        } else if (error.message.includes('Network') || error.message.includes('Failed to fetch')) {
          userMessage = 'Network error. Please check your internet connection.';
        } else if (error.message.includes('duplicate') || error.message.includes('already exists')) {
          userMessage = 'An employee with this ID already exists. Please use a different ID.';
        } else if (error.message.includes('No face detected')) {
          userMessage = 'No face detected in captured image. Please retake a clear photo.';
        } else if (error.message.includes('400') || error.message.includes('Bad Request')) {
          userMessage = 'Invalid data provided. Please check all fields and try again.';
        } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
          userMessage = 'Server error occurred. Please try again later.';
        }
      }

      showSnackbar(userMessage, 'error');
      setIsRegistering(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, pb: { xs: 2, sm: 4 }, background: '#fafbfc', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        <Box sx={{ px: { xs: 0.5, sm: 0 } }}>
          <Typography
            variant="h4"
            fontWeight={600}
            gutterBottom
            sx={{ mb: 0.5, fontSize: { xs: '1.55rem', sm: '2.15rem' }, lineHeight: 1.2 }}
          >
            Register New Employee
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: { xs: 2, sm: 2.5 }, fontSize: { xs: '0.88rem', sm: '0.95rem' } }}>
            Guided webcam enrollment captures face angles automatically, with manual upload fallback.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: { xs: 1.5, sm: 3 },
              mb: 2,
            }}
          >
            <TextField
              label="Employee Name"
              value={employeeName}
              onChange={(event) => setEmployeeName(event.target.value)}
              required
              placeholder="Employee Name"
              sx={{ background: '#fff', borderRadius: 2 }}
              InputLabelProps={{ sx: { fontWeight: 500, fontSize: 15 } }}
              inputProps={{ maxLength: 40, style: { fontSize: 15 } }}
            />
            <TextField
              label="Employee ID"
              value={employeeId}
              onChange={(event) =>{
              setEmployeeId(event.target.value);
              setEmployeeIdError(false);
            }}
            required
            error={employeeIdError}
              placeholder="Employee ID"
              sx={{ background: '#fff', borderRadius: 2 }}
              InputLabelProps={{ sx: { fontWeight: 500, fontSize: 15 } }}
              inputProps={{ maxLength: 100, style: { fontSize: 15 } }}
              inputRef={employeeIdInputRef}
            />
            <TextField
              label="Employee Code"
              value={employeeCode}
              onChange={(event) => setEmployeeCode(event.target.value)}
              required
              placeholder="Employee Code"
              sx={{ background: '#fff', borderRadius: 2 }}
              InputLabelProps={{ sx: { fontWeight: 500, fontSize: 15 } }}
              inputProps={{ maxLength: 100, style: { fontSize: 15 } }}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: { xs: 1.5, sm: 3 },
              mb: 2,
            }}
          >
            <FormControl fullWidth required sx={{ background: '#fff', borderRadius: 2 }}>
              <InputLabel>Company</InputLabel>
              <Select
                value={selectedCompanyId}
                label="Company"
                onChange={(event) => {
                  setSelectedCompanyId(event.target.value);
                  setSelectedBranchId('');
                  setSelectedDesignation('');
                  setSelectedDepartment('');
                }}
              >
                <MenuItem value="">Select Company</MenuItem>
                {companyOptions.map((cfg) => (
                  <MenuItem
                    key={cfg.config_id || cfg.company_id || cfg.access_token}
                    value={cfg.company_id || ''}
                  >
                    {cfg.company_name || 'Default Company'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ background: '#fff', borderRadius: 2 }} disabled={!selectedCompanyId}>
              <InputLabel>Branch</InputLabel>
              <Select
                value={selectedBranchId}
                label="Branch"
                onChange={(event) => {
                  setSelectedBranchId(event.target.value);
                  setSelectedDesignation('');
                  setSelectedDepartment('');
                }}
              >
                <MenuItem value="">All Branches</MenuItem>
                {branchOptions.map((branch) => (
                  <MenuItem key={branch.branch_id} value={branch.branch_id}>
                    {branch.branch_address || branch.branch_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              sx={{ background: '#fff', borderRadius: 2 }}
              disabled={!selectedCompanyId || designationOptions.length === 0}
            >
              <InputLabel>Designation</InputLabel>
              <Select
                value={selectedDesignation}
                label="Designation"
                onChange={(event) => setSelectedDesignation(event.target.value)}
              >
                <MenuItem value="">All Designations</MenuItem>
                {designationOptions.map((designation) => (
                  <MenuItem key={designation} value={designation}>
                    {designation}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              sx={{ background: '#fff', borderRadius: 2 }}
              disabled={!selectedCompanyId || departmentOptions.length === 0}
            >
              <InputLabel>Department</InputLabel>
              <Select
                value={selectedDepartment}
                label="Department"
                onChange={(event) => setSelectedDepartment(event.target.value)}
              >
                <MenuItem value="">All Departments</MenuItem>
                {departmentOptions.map((department) => (
                  <MenuItem key={department} value={department}>
                    {department}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ background: '#fff', borderRadius: 2, p: { xs: 1.5, sm: 2 }, mb: 2, border: '1px solid #e2e8f0' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Validity
            </Typography>
            <FormControlLabel
              sx={{ mb: 1 }}
              control={
                <Checkbox
                  checked={!!validity.unlimited}
                  onChange={(e) => setValidity((prev) => ({ ...prev, unlimited: e.target.checked }))}
                />
              }
              label="Unlimited Validity"
            />
            <TextField
              size="small"
              label="Validity (start → end)"
              value={
                validity.unlimited
                  ? ''
                  : (() => {
                      const vf = validity.validFrom;
                      const vt = validity.validTill;
                      if (!vf && !vt) return 'Select date time range';
                      try {
                        const s = vf ? new Date(vf) : null;
                        const e = vt ? new Date(vt) : null;
                        if (s && e && !Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime())) {
                          return `${format(s, 'MMM d, yyyy h:mm a')} → ${format(e, 'MMM d, yyyy h:mm a')}`;
                        }
                      } catch {
                        // ignore parse issues and fallback to raw text
                      }
                      return [vf, vt].filter(Boolean).join(' → ') || 'Click calendar to set';
                    })()
              }
              placeholder="Select range"
              onClick={() => {
                if (!validity.unlimited) setValidityPickerOpen(true);
              }}
              disabled={validity.unlimited}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      size="small"
                      disabled={validity.unlimited}
                      onClick={() => setValidityPickerOpen(true)}
                      aria-label="Open validity calendar"
                    >
                      <CalendarMonth fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': { cursor: validity.unlimited ? 'default' : 'pointer' },
              }}
            />
          </Box>

          <Box sx={{ background: '#fff', borderRadius: 3, p: { xs: 1.5, sm: 2, md: 3 }, border: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Face Enrollment
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                <Chip
                  color={requiredCaptured ? 'success' : 'warning'}
                  label={requiredCaptured ? 'Required Angles Ready' : (isRedirectRegistration ? 'Pending Front Capture' : 'Pending Required Angles')}
                  size="small"
                  sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                />
                <Button
                  variant="outlined"
                  onClick={handleRetakeAll}
                  disabled={!Object.values(capturedImages).some(Boolean)}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Retake All
                </Button>
                {!isEnrollmentActive ? (
                  <Button
                    variant="contained"
                    startIcon={<Videocam />}
                    onClick={() => {
                      setEnrollmentCameraDeviceId('');
                      setCameraError('');
                      setCaptureSessionNonce((previous) => previous + 1);
                      startEnrollment();
                    }}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    Start Enrollment
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<Replay />}
                    onClick={stopEnrollment}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    Pause Camera
                  </Button>
                )}
              </Box>
            </Box>

            {cameraError ? <Alert severity="error" sx={{ mb: 2 }}>{cameraError}</Alert> : null}

            <EnrollmentStepper
              steps={ENROLLMENT_STEPS}
              activeStep={activeStep}
              capturedMap={capturedImages}
              onStepSelect={(index) => {
                if (!isStepSelectable(index)) {
                  showSnackbar('Complete current required steps first.', 'warning');
                  return;
                }
                setActiveStep(index);
                if (!isEnrollmentActive) {
                  startEnrollment(index);
                }
              }}
            />

            <Collapse
              in={isEnrollmentActive}
              timeout={{ enter: 420, exit: 280 }}
              easing={{ enter: 'cubic-bezier(0.2, 0, 0, 1)', exit: 'cubic-bezier(0.4, 0, 1, 1)' }}
              unmountOnExit
            >
              <Fade in={isEnrollmentActive} timeout={{ enter: 360, exit: 180 }}>
                <Box sx={{ mt: 2 }}>
                  <WebcamCapture
                    key={`enrollment-capture-${captureSessionNonce}`}
                    stepKey={currentStep.key}
                    expectedDirection={currentStep.direction}
                    preferredDeviceId={enrollmentCameraDeviceId}
                    onPreferredDeviceChange={setEnrollmentCameraDeviceId}
                    onCaptured={handleCaptured}
                    onPermissionDenied={setCameraError}
                  />

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Live guidance validates face presence, centering, distance, head direction, and overlays face mesh.
                      For profile steps: left profile asks you to turn right, right profile asks you to turn left.
                      For Top View, tilt your head down slightly.
                    </Typography>
                    {currentStep.key === 'custom' ? (
                      <Button
                        size="small"
                        onClick={() => {
                          setIsEnrollmentActive(false);
                          showSnackbar('Top View skipped.', 'info');
                        }}
                        sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                      >
                        Skip Optional Step
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              </Fade>
            </Collapse>

            <Box
              sx={{
                mt: 2.5,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: 2,
              }}
            >
              {ENROLLMENT_STEPS.map((step) => (
                <CapturePreviewCard
                  key={step.key}
                  stepKey={step.key}
                  title={step.label}
                  required={isRedirectRegistration ? step.key === 'front' : step.required}
                  image={capturedImages[step.key]}
                  previewFrameRef={(node) => {
                    previewFrameRefs.current[step.key] = node;
                  }}
                  onCapture={() => startEnrollment(ENROLLMENT_STEPS.findIndex((entry) => entry.key === step.key))}
                  onRetake={() => handleRetake(step.key)}
                  onUpload={handleManualUpload(step.key)}
                  qualityResult={qualityResults[step.key]}
                />
              ))}
            </Box>

          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              onClick={registerEmployee}
              disabled={
                isRegistering ||
                !employeeName.trim() ||
                !employeeId.trim() ||
                !employeeCode.trim() ||
                !requiredCaptured
              }
              sx={{ width: { xs: '100%', sm: 260 }, fontWeight: 700, fontSize: 15 }}
              startIcon={
                isRegistering ? <CircularProgress size={20} color="inherit" /> : <CheckCircleOutline />
              }
            >
              {isRegistering ? 'Registering...' : 'Register Employee'}
            </Button>
          </Box>

          {Object.entries(capturedImages).some(([, image]) => Boolean(image)) ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
              Captures are currently stored in-memory as base64 and submitted to the existing enrollment API.
            </Typography>
          ) : null}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={validityPickerOpen}
          onClose={() => setValidityPickerOpen(false)}
          maxWidth={false}
          fullWidth={false}
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              overflow: 'auto',
              width: 'fit-content',
              maxWidth: 'calc(100vw - 24px)',
              m: { xs: 1.5, sm: 2 },
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1, py: 1.5 }}>
            <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
              Select validity range
            </Typography>
            <IconButton size="small" onClick={() => setValidityPickerOpen(false)} aria-label="Close">
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, width: 'fit-content', maxWidth: '100%', overflowX: 'auto' }}>
            <DateRangePickerContent
              dateRange={validityDateRange}
              onDateRangeChange={setValidityDateRange}
              disableMaxDate
              showTimeFields
              startTime={validityStartTime}
              endTime={validityEndTime}
              onStartTimeChange={setValidityStartTime}
              onEndTimeChange={setValidityEndTime}
            />
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                const today = new Date();
                setValidityDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
                setValidityStartTime('00:00');
                setValidityEndTime('23:59');
              }}
              sx={{ textTransform: 'none' }}
            >
              Reset
            </Button>
            <Button variant="outlined" onClick={() => setValidityPickerOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={applyValidityRangePicker}>
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      {captureHero ? (
        <Box
          sx={{
            pointerEvents: 'none',
            position: 'fixed',
            zIndex: 1500,
            left: captureHero.active ? captureHero.endRect.left : captureHero.startRect.left,
            top: captureHero.active ? captureHero.endRect.top : captureHero.startRect.top,
            width: captureHero.active ? captureHero.endRect.width : captureHero.startRect.width,
            height: captureHero.active ? captureHero.endRect.height : captureHero.startRect.height,
            transition: `all ${HERO_ANIMATION_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: captureHero.active
              ? '0 14px 28px rgba(2,132,199,0.22)'
              : '0 8px 22px rgba(15,23,42,0.28)',
            transform: captureHero.active ? 'scale(1)' : 'scale(0.9)',
          }}
        >
          <img
            src={captureHero.image}
            alt="Captured Hero Transition"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              background: '#e5e7eb',
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default EmployeeRegistration;
